"use client";

import React, {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";
import { createLogAdapter } from "@/lib/logger/console-adapter";
import { generateNonce } from "@/lib/utils/nonce-generator";
import { SessionKeyEncryption } from "@/lib/utils/session-key-encryption";
import { useIdentity } from "./IdentityContext";

interface LimitOrder {
	id: string;
	user_principal: string;
	token_id: string;
	status: "active" | "paused" | "completed" | "error" | "cancelled";
	parameters: {
		side: "buy" | "sell";
		price: string;
		amount: string;
		expires_in: number;
		slippage: number;
	};
	created_at: string;
	updated_at: string;
	strategy_name: { [key: string]: string };
}

interface LimitOrderContextType {
	orders: LimitOrder[];
	loading: boolean;
	error: string | null;

	// 操作方法
	createOrder: (tokenId: string, parameters: any) => Promise<boolean>;
	cancelOrder: (orderId: string) => Promise<boolean>;
	pauseOrder: (orderId: string) => Promise<boolean>;
	resumeOrder: (orderId: string) => Promise<boolean>;
	refreshOrders: () => Promise<void>;

	// 凭证管理
	hasValidCredentials: boolean;
	credentialsExpireAt: string | null;
	authorizeCredentials: () => Promise<boolean>;
	revokeCredentials: () => Promise<boolean>;
	checkCredentials: () => Promise<void>;
}

const LimitOrderContext = createContext<LimitOrderContextType | undefined>(
	undefined,
);
const uiLog = createLogAdapter("contexts/LimitOrderContext.tsx");

interface LimitOrderProviderProps {
	children: ReactNode;
}

export function LimitOrderProvider({ children }: LimitOrderProviderProps) {
	const { identity, isAuthenticated, principal } = useIdentity();

	const [orders, setOrders] = useState<LimitOrder[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [hasValidCredentials, setHasValidCredentials] = useState(false);
	const [credentialsExpireAt, setCredentialsExpireAt] = useState<string | null>(
		null,
	);

	// 检查凭证状态
	const checkCredentials = async () => {
		if (!isAuthenticated || !principal) {
			setHasValidCredentials(false);
			setCredentialsExpireAt(null);
			return;
		}

		try {
			const response = await fetch("/api/automation/credentials/check", {
				method: "GET",
				headers: {
					"X-User-Principal": principal,
				},
			});

			if (response.ok) {
				const data = await response.json();
				setHasValidCredentials(data.valid);
				setCredentialsExpireAt(data.expires_at);
			} else {
				setHasValidCredentials(false);
				setCredentialsExpireAt(null);
			}
		} catch (error) {
			uiLog.error("检查凭证失败:", error);
			setHasValidCredentials(false);
			setCredentialsExpireAt(null);
		}
	};

	// 授权凭证
	const authorizeCredentials = async (): Promise<boolean> => {
		if (!identity) return false;

		try {
			setLoading(true);
			setError(null);

			// 创建签名荷载
			const payload = {
				action: "AUTHORIZE_CREDENTIALS",
				timestamp: Date.now(),
				nonce: generateNonce(),
			};

			const payloadString = JSON.stringify(payload);
			const signature = await identity.sign(
				new TextEncoder().encode(payloadString),
			);
			const delegation = identity.getDelegation().toJSON();

			// 将签名转换为 Uint8Array（如果需要）
			const signatureArray =
				signature instanceof Uint8Array ? signature : new Uint8Array(signature);

			// 获取加密的会话私钥
			const decryptedPrivateKey = await SessionKeyEncryption.decryptSessionKey(
				principal!,
			);
			if (!decryptedPrivateKey) {
				throw new Error("无法获取会话私钥，请重新登录");
			}

			// 构建包含会话私钥的凭证数据
			const credentialData = {
				sessionPrivateKey: decryptedPrivateKey,
				delegationChain: delegation,
			};

			// 正确构建认证头
			const response = await fetch("/api/automation/credentials", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-Auth-Payload": btoa(payloadString),
					"X-Auth-Signature": btoa(String.fromCharCode(...signatureArray)),
					"X-Auth-Delegation": btoa(JSON.stringify(delegation)),
				},
				body: JSON.stringify({
					identity_data: JSON.stringify(credentialData),
				}),
			});

			if (response.ok) {
				await checkCredentials();
				return true;
			} else {
				const errorData = await response.json();
				setError(errorData.error || "凭证授权失败");
				return false;
			}
		} catch (error: any) {
			uiLog.error("授权凭证失败:", error);
			setError(error.message || "凭证授权失败");
			return false;
		} finally {
			setLoading(false);
		}
	};

	// 取消授权
	const revokeCredentials = async (): Promise<boolean> => {
		if (!identity || !principal) return false;

		try {
			setLoading(true);
			setError(null);

			// 创建签名荷载
			const payload = {
				action: "REVOKE_CREDENTIALS",
				timestamp: Date.now(),
				nonce: generateNonce(),
			};

			const payloadString = JSON.stringify(payload);
			const signature = await identity.sign(
				new TextEncoder().encode(payloadString),
			);
			const delegation = identity.getDelegation().toJSON();

			// 将签名转换为 Uint8Array（如果需要）
			const signatureArray =
				signature instanceof Uint8Array ? signature : new Uint8Array(signature);

			// 发送取消授权请求
			const response = await fetch("/api/automation/credentials/revoke", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-Auth-Payload": btoa(payloadString),
					"X-Auth-Signature": btoa(String.fromCharCode(...signatureArray)),
					"X-Auth-Delegation": btoa(JSON.stringify(delegation)),
				},
			});

			if (response.ok) {
				// 清除本地凭证状态
				setHasValidCredentials(false);
				setCredentialsExpireAt(null);
				// 刷新订单状态以显示暂停的订单
				await refreshOrders();
				return true;
			} else {
				const errorData = await response.json();
				setError(errorData.error || "取消授权失败");
				return false;
			}
		} catch (error: any) {
			uiLog.error("取消授权失败:", error);
			setError(error.message || "取消授权失败");
			return false;
		} finally {
			setLoading(false);
		}
	};

	// 获取用户订单
	const refreshOrders = async () => {
		if (!isAuthenticated || !principal) {
			setOrders([]);
			return;
		}

		try {
			setLoading(true);
			setError(null);

			const response = await fetch("/api/automation/orders", {
				method: "GET",
				headers: {
					"X-User-Principal": principal,
				},
			});

			if (response.ok) {
				const data = await response.json();
				setOrders(data.orders || []);
			} else {
				const errorData = await response.json();
				setError(errorData.error || "获取订单失败");
			}
		} catch (error: any) {
			uiLog.error("获取订单失败:", error);
			setError(error.message || "获取订单失败");
		} finally {
			setLoading(false);
		}
	};

	// 创建订单
	const createOrder = async (
		tokenId: string,
		parameters: any,
	): Promise<boolean> => {
		if (!identity) return false;

		try {
			setLoading(true);
			setError(null);

			// 创建签名荷载
			const payload = {
				action: "CREATE_STRATEGY_INSTANCE",
				strategy_type_key: "LIMIT_ORDER",
				token_id: tokenId,
				parameters,
				timestamp: Date.now(),
				nonce: generateNonce(),
			};

			const payloadString = JSON.stringify(payload);
			const signature = await identity.sign(
				new TextEncoder().encode(payloadString),
			);
			const delegation = identity.getDelegation().toJSON();

			// 将签名转换为 Uint8Array（如果需要）
			const signatureArray =
				signature instanceof Uint8Array ? signature : new Uint8Array(signature);

			const response = await fetch("/api/automation/instances", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-Auth-Payload": btoa(payloadString),
					"X-Auth-Signature": btoa(String.fromCharCode(...signatureArray)),
					"X-Auth-Delegation": btoa(JSON.stringify(delegation)),
				},
				body: JSON.stringify({
					strategy_type_key: "LIMIT_ORDER",
					token_id: tokenId,
					parameters,
				}),
			});

			if (response.ok) {
				await refreshOrders();
				return true;
			} else {
				const errorData = await response.json();
				setError(errorData.error || "创建订单失败");
				return false;
			}
		} catch (error: any) {
			uiLog.error("创建订单失败:", error);
			setError(error.message || "创建订单失败");
			return false;
		} finally {
			setLoading(false);
		}
	};

	// 取消订单
	const cancelOrder = async (orderId: string): Promise<boolean> => {
		if (!identity) return false;

		try {
			setLoading(true);
			setError(null);

			const payload = {
				action: "CANCEL_STRATEGY_INSTANCE",
				instance_id: orderId,
				timestamp: Date.now(),
				nonce: generateNonce(),
			};

			const payloadString = JSON.stringify(payload);
			const signature = await identity.sign(
				new TextEncoder().encode(payloadString),
			);
			const delegation = identity.getDelegation().toJSON();

			// 将签名转换为 Uint8Array（如果需要）
			const signatureArray =
				signature instanceof Uint8Array ? signature : new Uint8Array(signature);

			const response = await fetch(
				`/api/automation/instances/${orderId}/cancel`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						"X-Auth-Payload": btoa(payloadString),
						"X-Auth-Signature": btoa(String.fromCharCode(...signatureArray)),
						"X-Auth-Delegation": btoa(JSON.stringify(delegation)),
					},
				},
			);

			if (response.ok) {
				setOrders((currentOrders) =>
					currentOrders.map((order) =>
						order.id === orderId
							? {
									...order,
									status: "cancelled",
									updated_at: new Date().toISOString(),
								}
							: order,
					),
				);
				void refreshOrders().catch((refreshError) => {
					uiLog.error("取消订单后刷新订单失败:", refreshError);
				});
				return true;
			} else {
				const errorData = await response.json();
				setError(errorData.error || "取消订单失败");
				return false;
			}
		} catch (error: any) {
			uiLog.error("取消订单失败:", error);
			setError(error.message || "取消订单失败");
			return false;
		} finally {
			setLoading(false);
		}
	};

	// 暂停订单
	const pauseOrder = async (orderId: string): Promise<boolean> => {
		if (!identity) return false;
		return await updateOrderStatus(orderId, "paused");
	};

	// 恢复订单
	const resumeOrder = async (orderId: string): Promise<boolean> => {
		if (!identity) return false;
		return await updateOrderStatus(orderId, "active");
	};

	// 更新订单状态的通用方法
	const updateOrderStatus = async (
		orderId: string,
		status: string,
	): Promise<boolean> => {
		try {
			setLoading(true);
			setError(null);

			const payload = {
				action: "UPDATE_STRATEGY_INSTANCE",
				instance_id: orderId,
				status,
				timestamp: Date.now(),
				nonce: generateNonce(),
			};

			const payloadString = JSON.stringify(payload);
			const signature = await identity!.sign(
				new TextEncoder().encode(payloadString),
			);
			const delegation = identity!.getDelegation().toJSON();

			// 将签名转换为 Uint8Array（如果需要）
			const signatureArray =
				signature instanceof Uint8Array ? signature : new Uint8Array(signature);

			const response = await fetch(
				`/api/automation/instances/${orderId}/status`,
				{
					method: "PUT",
					headers: {
						"Content-Type": "application/json",
						"X-Auth-Payload": btoa(payloadString),
						"X-Auth-Signature": btoa(String.fromCharCode(...signatureArray)),
						"X-Auth-Delegation": btoa(JSON.stringify(delegation)),
					},
					body: JSON.stringify({ status }),
				},
			);

			if (response.ok) {
				await refreshOrders();
				return true;
			} else {
				const errorData = await response.json();
				setError(errorData.error || "更新订单状态失败");
				return false;
			}
		} catch (error: any) {
			uiLog.error("更新订单状态失败:", error);
			setError(error.message || "更新订单状态失败");
			return false;
		} finally {
			setLoading(false);
		}
	};

	// 监听身份变化
	useEffect(() => {
		if (isAuthenticated && principal) {
			checkCredentials();
			refreshOrders();
		} else {
			setOrders([]);
			setHasValidCredentials(false);
			setCredentialsExpireAt(null);
		}
	}, [isAuthenticated, principal]);

	const value: LimitOrderContextType = {
		orders,
		loading,
		error,
		createOrder,
		cancelOrder,
		pauseOrder,
		resumeOrder,
		refreshOrders,
		hasValidCredentials,
		credentialsExpireAt,
		authorizeCredentials,
		revokeCredentials,
		checkCredentials,
	};

	return (
		<LimitOrderContext.Provider value={value}>
			{children}
		</LimitOrderContext.Provider>
	);
}

export function useLimitOrder() {
	const context = useContext(LimitOrderContext);
	if (context === undefined) {
		throw new Error("useLimitOrder must be used within a LimitOrderProvider");
	}
	return context;
}
